import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../components/AuthContext';
import { FaSearch, FaFilter, FaDownload, FaEye, FaTasks, FaTimes, FaClock, FaChevronLeft, FaChevronRight, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ActivityLogsReports = () => {
  const { user } = useContext(AuthContext);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logType, setLogType] = useState('activitynew');  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [filterDateFrom, setFilterDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [filterDateTo, setFilterDateTo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterItem, setFilterItem] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(15);
  const [employees, setEmployees] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [additionalRemarks, setAdditionalRemarks] = useState('');
  const [extraRemark, setExtraRemark] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showExtraRemarkModal, setShowExtraRemarkModal] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    draft: 0,
    service: 0,
    quotationIssued: 0
  });
  const [pieChartData, setPieChartData] = useState({
    labels: [],
    datasets: []
  });
  const [lineChartData, setLineChartData] = useState({
    labels: [],
    datasets: []
  });

  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const interval = setInterval(() => {
      setIsDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const chartColors = useMemo(() => ({
    text: isDarkMode ? '#d1d5db' : '#374151',
    grid: isDarkMode ? '#374151' : '#e5e7eb',
  }), [isDarkMode]);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const statusOptions = ['Pending', 'Completed', 'Cancelled', 'Draft', 'Service', 'Quotation issued'];

  const prepareLineChartData = () => {
    // Get last 7 days
    const last7Days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      last7Days.push({
        date: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }

    // Count logs created per day from all activity logs (not just current page)
    const logCounts = last7Days.map(day => {
      const count = activityLogs.filter(log => {
        const logDate = new Date(log.createdAt).toISOString().split('T')[0];
        return logDate === day.date;
      }).length;
      return count;
    });

    setLineChartData({
      labels: last7Days.map(day => day.label),
      datasets: [
        {
          label: 'Activity Logs Created',
          data: logCounts,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  const prepareLineChartDataFromAPI = (lineData) => {
    setLineChartData({
      labels: lineData.labels,
      datasets: [
        {
          label: 'Activity Logs Created',
          data: lineData.data,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!user?.user?._id) return;
    fetchActivityLogs();
    fetchEmployees();
  }, [user, currentPage, logType, filterDateFrom, filterDateTo, filterStatus, filterEmployee, filterCategory, filterItem, debouncedSearch]);

  const fetchActivityLogs = async () => {
    if (!user?.user?._id) return;
    setLoading(true);
    try {
      const params = {
        companyHeadId: user.user._id,
        logType,
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        dateFrom: filterDateFrom,
        dateTo: filterDateTo,
        status: filterStatus,
        employeeId: filterEmployee,
        category: filterCategory,
        item: filterItem
      };

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get(`${API_BASE}/reports/activitylogs`, { params });
      
      if (response.data.success) {
        setActivityLogs(response.data.data);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalCount(response.data.pagination.totalCount || 0);
        
        // Set statistics and pie chart data from response
        if (response.data.statistics) {
          setStatistics(response.data.statistics);
        }
        if (response.data.pieChartData) {
          preparePieChartData(response.data.pieChartData);
        }
        if (response.data.lineChartData) {
          prepareLineChartDataFromAPI(response.data.lineChartData);
        }
      } else {
        toast.error('Failed to fetch activity logs');
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const preparePieChartData = (pieData) => {
    // pieData is an array of { name, value, color } from the backend
    const filtered = pieData.filter(item => item.value > 0);
    setPieChartData({
      labels: filtered.map(item => item.name),
      datasets: [{
        data: filtered.map(item => item.value),
        backgroundColor: filtered.map(item => item.color)
      }]
    });
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_BASE}/reports/activitylogs/employees/list?companyHeadId=${user.user._id}`);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleStatusUpdate = (log) => {
    // Allow viewing details for all logs, but only allow status updates for pending logs
    // For pending logs, check if status has already been updated
    if (log.status === 'Pending' && log.isStatusUpdated) {
      toast.warning('Status has already been updated and cannot be changed again');
      return;
    }

    setSelectedLog(log);
    // Set initial new status to first non-pending option for pending logs
    if (log.status === 'Pending') {
      setNewStatus('Completed'); // Default to Completed for pending logs
    } else {
      setNewStatus(log.status);
    }
    setAdditionalRemarks('');
    setShowStatusModal(true);
  };

  const handleExtraRemark = (log) => {
    // Only allow adding extra remarks to pending logs
    if (log.status !== 'Pending' && log.status !== 'Quotation issued') {
      toast.warning('Extra remarks can only be added to pending logs');
      return;
    }

    setSelectedLog(log);
    setExtraRemark('');
    setShowExtraRemarkModal(true);
  };

  const handleExtraRemarkSubmit = async () => {
    if (!extraRemark.trim()) {
      toast.error('Please enter an extra remark');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE}/reports/activitylogs/${selectedLog._id}/extraremarks`, {
        extraRemark,
        logType,
        companyHeadId: user.user._id
      });

      if (response.data.success) {
        toast.success('Extra remark added successfully');
        setShowExtraRemarkModal(false);
        fetchActivityLogs();
      } else {
        toast.error(response.data.message || 'Failed to add extra remark');
      }
    } catch (error) {
      console.error('Error adding extra remark:', error);
      toast.error(error.response?.data?.message || 'Failed to add extra remark');
    }
  };

  const handleStatusSubmit = async () => {
    // Only allow status updates for pending logs
    if (selectedLog.status !== 'Pending') {
      toast.error('Status can only be updated for pending logs');
      return;
    }

    // Validate that new status is not Pending
    if (newStatus === 'Pending') {
      toast.error('Status cannot be changed back to Pending');
      return;
    }

    // Validate that status is actually changing
    if (newStatus === selectedLog.status && !additionalRemarks.trim()) {
      toast.warning('Please change the status or add additional remarks');
      return;
    }

    try {
      const response = await axios.put(`${API_BASE}/reports/activitylogs/${selectedLog._id}/status`, {
        status: newStatus,
        additionalRemarks,
        logType,
        companyHeadId: user.user._id
      });

      if (response.data.success) {
        toast.success('Status updated successfully');
        setShowStatusModal(false);
        fetchActivityLogs();
      } else {
        toast.error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        companyHeadId: user.user._id,
        logType,
        search: debouncedSearch,
        dateFrom: filterDateFrom,
        dateTo: filterDateTo,
        status: filterStatus,
        employeeId: filterEmployee,
        category: filterCategory,
        item: filterItem
      };

      const response = await axios.get(`${API_BASE}/reports/activitylogs/export/csv`, { 
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-${logType}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Activity logs exported successfully');
    } catch (error) {
      console.error('Error exporting activity logs:', error);
      toast.error('Failed to export activity logs');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString()
    };
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Draft': 'bg-gray-100 text-gray-800',
      'Service': 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      'Reference': 'bg-teal-100 text-teal-800',
      'Guest': 'bg-cyan-100 text-cyan-800',
      'Customer': 'bg-orange-100 text-orange-800',
      'Dealer': 'bg-purple-100 text-purple-800'
    };
    return categoryColors[category] || 'bg-gray-100 text-gray-800';
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    const now = new Date();
    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    setFilterDateFrom(monthStart);
    setFilterDateTo(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`);
    setFilterStatus('');
    setFilterEmployee('');
    setFilterCategory('');
    setFilterItem('');
    setCurrentPage(1);
  };

  const inputClass = `px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full transition-colors ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 hover:border-gray-500' : 'border-gray-300 bg-white text-gray-800 placeholder-gray-500 hover:border-gray-400'}`;

  const activeFilterChips = [
    searchTerm && { label: `Search: "${searchTerm}"`, clear: () => setSearchTerm('') },
    filterDateFrom && { label: `From: ${filterDateFrom}`, clear: () => setFilterDateFrom('') },
    filterDateTo && { label: `To: ${filterDateTo}`, clear: () => setFilterDateTo('') },
    filterStatus && { label: `Status: ${filterStatus}`, clear: () => setFilterStatus('') },
    filterEmployee && { label: `Employee: ${employees.find(e => e._id === filterEmployee)?.name || 'Selected'}`, clear: () => setFilterEmployee('') },
    filterCategory && { label: `Category: ${filterCategory}`, clear: () => setFilterCategory('') },
    filterItem && { label: `Item: ${filterItem}`, clear: () => setFilterItem('') }
  ].filter(Boolean);

  const setDatePreset = (preset) => {
    const now = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = fmt(now);
    if (preset === 'today') {
      setFilterDateFrom(today);
      setFilterDateTo(today);
    } else if (preset === '7days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      setFilterDateFrom(fmt(start));
      setFilterDateTo(today);
    } else if (preset === 'month') {
      setFilterDateFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
      setFilterDateTo(today);
    } else if (preset === 'lastMonth') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      setFilterDateFrom(fmt(start));
      setFilterDateTo(fmt(end));
    }
    setCurrentPage(1);
  };

  return (
    <div className={`p-6 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white'}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold flex items-center ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          <FaClock className="mr-3 text-blue-600" />
          Activity Logs Reports
        </h2>
        <button
          onClick={handleExport}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-700 transition-colors"
        >
          <FaDownload className="mr-2" />
          Export CSV
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Total Logs</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{statistics.total || totalCount}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>All matching records</p>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-800' : 'bg-yellow-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>Pending</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{statistics.pending || 0}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
            Current page: {activityLogs.filter(log => log.status === 'Pending').length}
          </p>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/30 border border-green-800' : 'bg-green-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>Completed</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{statistics.completed || 0}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
            Current page: {activityLogs.filter(log => log.status === 'Completed').length}
          </p>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/30 border border-red-800' : 'bg-red-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>Cancelled</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{statistics.cancelled || 0}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
            Current page: {activityLogs.filter(log => log.status === 'Cancelled').length}
          </p>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>Draft</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{statistics.draft || 0}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Current page: {activityLogs.filter(log => log.status === 'Draft').length}
          </p>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-purple-900/30 border border-purple-800' : 'bg-purple-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>Service</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{statistics.service || 0}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
            Current page: {activityLogs.filter(log => log.status === 'Service').length}
          </p>
        </div>
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-indigo-900/30 border border-indigo-800' : 'bg-indigo-50'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>Quotation Issued</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{statistics.quotationIssued || 0}</p>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
            Current page: {activityLogs.filter(log => log.status === 'Quotation issued').length}
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Line Chart */}
        <div className={`p-6 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Activity Logs Created - Last 7 Days</h3>
          <div className="h-64">
            <Line 
              data={lineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: chartColors.text },
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { stepSize: 1, color: chartColors.text },
                    grid: { color: chartColors.grid },
                  },
                  x: {
                    ticks: { color: chartColors.text },
                    grid: { color: chartColors.grid },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Pie Chart */}
        <div className={`p-6 rounded-lg shadow-lg ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Distribution</h3>
          <div className="h-64">
            <Pie 
              data={pieChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      boxWidth: 12,
                      padding: 15,
                      color: chartColors.text,
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.raw;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Log Type Selection and Filters */}
      <div className={`rounded-xl shadow-sm mb-6 overflow-hidden ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        {/* Search Bar Row */}
        <div className={`p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <FaSearch className={`absolute left-4 top-1/2 -translate-y-1/2 ${searchTerm ? 'text-blue-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search by ID, remarks, guest/reference name or number..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className={`${inputClass} pl-11 py-2.5 ${searchTerm ? 'pr-9' : 'pr-4'} rounded-xl`}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-100 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                  title="Clear search"
                >
                  <FaTimes size={13} />
                </button>
              )}
            </div>

            <select
              value={logType}
              onChange={(e) => {
                setLogType(e.target.value);
                setCurrentPage(1);
              }}
              className={`px-3 py-2.5 border rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors cursor-pointer ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 hover:border-gray-500' : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'}`}
            >
              <option value="activity">Activity Logs</option>
              <option value="activitynew">Activity Logs New</option>
            </select>

            <button
              onClick={() => setShowFilters(prev => !prev)}
              className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${showFilters || activeFilterChips.length > 0
                ? isDarkMode
                  ? 'bg-blue-900/40 text-blue-300 border border-blue-800 hover:bg-blue-900/60'
                  : 'bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
                  : 'bg-gray-50 text-gray-600 border border-gray-300 hover:bg-gray-100'
              }`}
            >
              <FaFilter className="mr-2" size={13} />
              Filters
              {activeFilterChips.length > 0 && (
                <span className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'}`}>
                  {activeFilterChips.length}
                </span>
              )}
              {showFilters ? <FaChevronUp className="ml-2" size={12} /> : <FaChevronDown className="ml-2" size={12} />}
            </button>
          </div>
        </div>

        {/* Collapsible Advanced Filters */}
        {showFilters && (
          <div className={`px-4 pb-4 pt-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            {/* Date Presets */}
            <div className="flex flex-wrap items-center gap-2 mt-3 mb-4">
              <span className={`text-xs font-semibold uppercase tracking-wide mr-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Quick Range:</span>
              {[
                { key: 'today', label: 'Today' },
                { key: '7days', label: 'Last 7 Days' },
                { key: 'month', label: 'This Month' },
                { key: 'lastMonth', label: 'Last Month' }
              ].map(preset => (
                <button
                  key={preset.key}
                  onClick={() => setDatePreset(preset.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-blue-900/40 hover:text-blue-300 hover:border-blue-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {/* Filter Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>From Date</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => {
                    setFilterDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>To Date</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => {
                    setFilterDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={inputClass}
                >
                  <option value="">All Status</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Employee</label>
                <select
                  value={filterEmployee}
                  onChange={(e) => {
                    setFilterEmployee(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={inputClass}
                >
                  <option value="">All Employees</option>
                  {employees.map(employee => (
                    <option key={employee._id} value={employee._id}>{employee.name}</option>
                  ))}
                </select>
              </div>

              {logType === 'activitynew' && (
                <>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</label>
                    <select
                      value={filterCategory}
                      onChange={(e) => {
                        setFilterCategory(e.target.value);
                        setCurrentPage(1);
                      }}
                      className={inputClass}
                    >
                      <option value="">All Categories</option>
                      <option value="Reference">Reference</option>
                      <option value="Guest">Guest</option>
                      <option value="Customer">Customer</option>
                      <option value="Dealer">Dealer</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Item</label>
                    <select
                      value={filterItem}
                      onChange={(e) => {
                        setFilterItem(e.target.value);
                        setCurrentPage(1);
                      }}
                      className={inputClass}
                    >
                      <option value="">All Items</option>
                      <option value="Tiles">Tiles</option>
                      <option value="Sanitary Ware">Sanitary Ware</option>
                      <option value="Kithen">Kitchen</option>
                      <option value="Doors">Doors</option>
                      <option value="Windows">Windows</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Active Filter Chips */}
        {activeFilterChips.length > 0 && (
          <div className={`flex flex-wrap items-center gap-2 px-4 py-3 border-t ${isDarkMode ? 'border-gray-700 bg-gray-750 bg-opacity-50' : 'border-gray-100 bg-gray-50'}`}>
            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Active:</span>
            {activeFilterChips.map((chip, idx) => (
              <span
                key={idx}
                className={`inline-flex items-center pl-3 pr-1.5 py-1 text-xs font-medium rounded-full ${isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}
              >
                {chip.label}
                <button
                  onClick={chip.clear}
                  className={`ml-1.5 p-0.5 rounded-full transition-colors ${isDarkMode ? 'hover:bg-blue-800/60' : 'hover:bg-blue-100'}`}
                  title="Remove"
                >
                  <FaTimes size={10} />
                </button>
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className={`ml-1 text-xs font-medium underline underline-offset-2 transition-colors ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Activity Logs Table */}
      <div className="overflow-x-auto">
        <table className={`min-w-full border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                ID
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Date
              </th>
              {logType === 'activity' ? (
                <>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    Project
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    Contact Person
                  </th>
                </>
              ) : (
                <>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    Guest/Reference
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    Category
                  </th>
                </>
              )}
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Status
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Created By
              </th>
              <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
            {loading ? (
              <tr>
                <td colSpan={7} className={`px-6 py-4 text-center ${isDarkMode ? 'text-gray-300' : ''}`}>
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading activity logs...</span>
                  </div>
                </td>
              </tr>
            ) : activityLogs.length === 0 ? (
              <tr>
                <td colSpan={7} className={`px-6 py-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No activity logs found
                </td>
              </tr>
            ) : (
              activityLogs.map((log, index) => {
                const { date } = formatDateTime(log.date);
                return (
                  <tr key={log._id || index} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {log.id}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {date}
                    </td>
                    {logType === 'activity' ? (
                      <>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {log.project}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {log.contactPerson}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {log.guest?.guestName || 
                           log.reference?.name ||
                           log.customer?.name ||
                           log.dealer?.businessName ||
                           'No Contact'}
                          {log.guest && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Guest: {log.guest.phoneNo}</div>
                          )}
                          {log.reference && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Ref: {log.reference.phoneNumber}</div>
                          )}
                          {log.customer && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Customer: {log.customer.phone}</div>
                          )}
                          {log.dealer && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dealer: {log.dealer.phone}</div>
                          )}
                          {log.guest?.station?.name && (
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Station: {log.guest.station.name}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(log.category)}`}>
                            {log.category}
                          </span>
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                      {log.isStatusUpdated && (
                        <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(Updated)</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {log.createdBy?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleStatusUpdate(log)}
                          className="text-blue-600 hover:text-blue-800"
                          title={
                            log.status === 'Completed'
                              ? 'View Details'
                              : log.status === 'Pending' && !log.isStatusUpdated
                              ? 'Update Status'
                              : log.status === 'Pending' && log.isStatusUpdated
                              ? 'View Details (Status already updated)'
                              : 'View Details'
                          }
                        >
                          <FaTasks />
                        </button>
                        
                        {(log.status === 'Pending' || (log.status === 'Quotation issued' && log.isStatusUpdated)) && (
                          <button
                            onClick={() => handleExtraRemark(log)}
                            className="text-green-600 hover:text-green-800"
                            title="Add Extra Remark"
                          >
                            <FaPlus />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
            Showing page {currentPage} of {totalPages} ({totalCount} total records)
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 border rounded-md text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
            >
              <FaChevronLeft className="mr-1" />
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, currentPage - 2) + i;
              if (pageNumber > totalPages) return null;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 border rounded-md text-sm font-medium ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 border rounded-md text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-200' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
            >
              Next
              <FaChevronRight className="ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className={`flex justify-between items-center p-6 border-b ${isDarkMode ? 'border-gray-700 bg-gradient-to-r from-blue-900/40 to-indigo-900/40' : 'border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
              <div>
                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  {selectedLog.status === 'Pending' ? 'Update Activity Log Status' : 'Activity Log Details'}
                </h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Log ID: <span className="font-semibold text-blue-600">{selectedLog.id}</span> - 
                  <span className={`font-semibold ml-1 ${
                    selectedLog.status === 'Completed' ? 'text-green-600' : 
                    selectedLog.status === 'Pending' ? 'text-yellow-600' : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {selectedLog.status}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setShowStatusModal(false)}
                className={`rounded-lg p-2 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Log Details Section */}
              <div className={`rounded-xl p-6 mb-6 border ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                <div className="flex items-center mb-4">
                  <FaEye className="text-blue-600 mr-2" />
                  <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Activity Log Details</h4>
                </div>
                
                {logType === 'activity' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Project Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Project:</span>
                            <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.project}</span>
                          </div>
                          <div className="flex">
                            <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Date:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{formatDateTime(selectedLog.date).date}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Contact Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className={`font-medium w-32 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Contact Person:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.contactPerson}</span>
                          </div>
                          <div className="flex">
                            <span className={`font-medium w-32 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Contact Number:</span>
                            <span className={`font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.contactNumber}</span>
                          </div>
                          <div className="flex">
                            <span className={`font-medium w-32 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Station:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.station?.name || 'N/A'}</span>
                          </div>
                          <div className="flex">
                            <span className={`font-medium w-32 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reference:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.referenceName}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Requirements</h5>
                        <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                          <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {Array.isArray(selectedLog.requirements) ? selectedLog.requirements.join(', ') : selectedLog.requirements}
                          </p>
                        </div>
                      </div>
                      
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Additional Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.createdBy?.name || 'Unknown'}</span>
                          </div>
                          {selectedLog.statusUpdatedBy && (
                            <div className="flex">
                              <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Updated By:</span>
                              <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Full Width Remarks */}
                    <div className="lg:col-span-2">
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Remarks</h5>
                        <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                          <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.remarks}</p>
                        </div>
                        {selectedLog.additionalRemarks && (
                          <>
                            <h5 className={`font-semibold mb-2 mt-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Additional Remarks</h5>
                            <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                              <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.additionalRemarks}</p>
                            </div>
                          </>
                        )}
                        {selectedLog.extraRemarks && selectedLog.extraRemarks.length > 0 && (
                          <>
                            <h5 className={`font-semibold mb-2 mt-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Extra Remarks</h5>
                            <div className="space-y-2">
                              {selectedLog.extraRemarks.map((remark, index) => (
                                <div key={index} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-yellow-900/30 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                                  <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{remark.text}</p>
                                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Added on: {new Date(remark.date).toLocaleString()}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Contact Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Name:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                              {selectedLog.guest?.guestName || 
                               selectedLog.reference?.name ||
                               selectedLog.customer?.name ||
                               selectedLog.dealer?.businessName ||
                               'No Contact'}
                            </span>
                          </div>
                          {selectedLog.guest && (
                            <>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</span>
                                <span className={`font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.guest.phoneNo}</span>
                              </div>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Station:</span>
                                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.guest.station?.name || selectedLog.guest.station}</span>
                              </div>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Reference:</span>
                                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.guest.reference}</span>
                              </div>
                            </>
                          )}
                          {selectedLog.reference && (
                            <>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</span>
                                <span className={`font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.reference.phoneNumber}</span>
                              </div>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type:</span>
                                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.reference.type}</span>
                              </div>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Class:</span>
                                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.reference.classification}</span>
                              </div>
                            </>
                          )}
                          {selectedLog.customer && (
                            <>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</span>
                                <span className={`font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.customer.phone}</span>
                              </div>
                              {selectedLog.customer.station && (
                                <div className="flex">
                                  <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Station:</span>
                                  <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.customer.station.name || selectedLog.customer.station}</span>
                                </div>
                              )}
                            </>
                          )}
                          {selectedLog.dealer && (
                            <>
                              <div className="flex">
                                <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Phone:</span>
                                <span className={`font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.dealer.phone}</span>
                              </div>
                              {selectedLog.dealer.station && (
                                <div className="flex">
                                  <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Station:</span>
                                  <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.dealer.station.name || selectedLog.dealer.station}</span>
                                </div>
                              )}
                            </>
                          )}
                          <div className="flex">
                            <span className={`font-medium w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Category:</span>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(selectedLog.category)}`}>
                              {selectedLog.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Additional Information</h5>
                        <div className="space-y-2">
                          <div className="flex">
                            <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.createdBy?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex">
                            <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Date:</span>
                            <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{formatDateTime(selectedLog.date).date}</span>
                          </div>
                          {selectedLog.statusUpdatedBy && (
                            <div className="flex">
                              <span className={`font-medium w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Updated By:</span>
                              <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.statusUpdatedBy.username}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Items</h5>
                        <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                          <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {Array.isArray(selectedLog.items) ? selectedLog.items.join(', ') : selectedLog.items}
                          </p>
                        </div>
                      </div>
                      
                      {(selectedLog.interior || selectedLog.architect) && (
                        <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                          <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Professional Contacts</h5>
                          <div className="space-y-2">
                            {selectedLog.interior && (
                              <div className="flex">
                                <span className={`font-medium w-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Interior:</span>
                                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.interior.name} - {selectedLog.interior.phoneNumber}</span>
                              </div>
                            )}
                            {selectedLog.architect && (
                              <div className="flex">
                                <span className={`font-medium w-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Architect:</span>
                                <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.architect.name} - {selectedLog.architect.phoneNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Full Width Remarks */}
                    <div className="lg:col-span-2">
                      <div className={`rounded-lg p-4 shadow-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 text-lg border-b pb-2 ${isDarkMode ? 'text-gray-200 border-gray-600' : 'text-gray-700 border-gray-200'}`}>Remarks</h5>
                        <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                          <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.remarks}</p>
                        </div>
                        {selectedLog.additionalRemarks && (
                          <>
                            <h5 className={`font-semibold mb-2 mt-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Additional Remarks</h5>
                            <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                              <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLog.additionalRemarks}</p>
                            </div>
                          </>
                        )}
                        {selectedLog.extraRemarks && selectedLog.extraRemarks.length > 0 && (
                          <>
                            <h5 className={`font-semibold mb-2 mt-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Extra Remarks</h5>
                            <div className="space-y-2">
                              {selectedLog.extraRemarks.map((remark, index) => (
                                <div key={index} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-yellow-900/30 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                                  <p className={`leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{remark.text}</p>
                                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Added on: {new Date(remark.date).toLocaleString()}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Conditional Status Update Section for Pending Logs */}
              {selectedLog.status === 'Pending' && !selectedLog.isStatusUpdated && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border-blue-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'}`}>
                  <div className="flex items-center mb-4">
                    <FaTasks className="text-blue-600 mr-2" />
                    <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Update Status</h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Status</label>
                      <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                          {selectedLog.status}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>New Status *</label>
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'}`}
                        required
                      >
                        {statusOptions.filter(status => status !== 'Pending').map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Note: Status cannot be changed back to Pending</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Additional Remarks</label>
                    <textarea
                      value={additionalRemarks}
                      onChange={(e) => setAdditionalRemarks(e.target.value)}
                      className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300'}`}
                      rows="4"
                      placeholder="Add any additional remarks about this status update..."
                    ></textarea>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Optional: Provide additional context for the status change</p>
                  </div>

                  <div className={`mt-4 p-4 border rounded-lg ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-center">
                      <div className="text-yellow-600 mr-2">⚠️</div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>Important:</p>
                        <p className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>Once you update the status, it cannot be changed again. Please ensure the status change is correct.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Information for Already Updated Pending Logs */}
              {selectedLog.status === 'Pending' && selectedLog.isStatusUpdated && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-yellow-800' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200'}`}>
                  <div className="flex items-center mb-4">
                    <FaTasks className="text-yellow-600 mr-2" />
                    <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Information</h4>
                  </div>
                  
                  <div className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-700 border-yellow-800' : 'bg-white border-yellow-200'}`}>
                    <p className={`font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                      This pending log has already been updated and cannot be modified again.
                    </p>
                    {selectedLog.statusUpdatedAt && (
                      <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Updated on: {new Date(selectedLog.statusUpdatedAt).toLocaleString()}
                      </p>
                    )}
                    {selectedLog.statusUpdatedBy && (
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Updated by: {selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}
                      </p>
                    )}
                  </div>

                  {selectedLog.additionalRemarks && (
                    <div className="mt-4">
                      <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Update Remarks</label>
                      <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                        <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.additionalRemarks}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Status Information Section - Read Only for Completed Logs */}
              {selectedLog.status === 'Completed' && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-green-800' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'}`}>
                <div className="flex items-center mb-4">
                  <FaTasks className="text-green-600 mr-2" />
                  <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Status</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Update Remarks</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated By</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Status Information Section - Read Only for Cancelled Logs */}
              {selectedLog.status === 'Cancelled' && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-red-900/20 to-pink-900/20 border-red-800' : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'}`}>
                <div className="flex items-center mb-4">
                  <FaTasks className="text-red-600 mr-2" />
                  <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Status</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Update Remarks</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated By</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Status Information Section - Read Only for Draft Logs */}
              {selectedLog.status === 'Draft' && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-gray-700 to-slate-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200'}`}>
                <div className="flex items-center mb-4">
                  <FaTasks className={isDarkMode ? 'text-gray-400 mr-2' : 'text-gray-600 mr-2'} />
                  <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Status</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Update Remarks</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated By</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Status Information Section - Read Only for Service Logs */}
              {selectedLog.status === 'Service' && (
                <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border-purple-800' : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200'}`}>
                <div className="flex items-center mb-4">
                  <FaTasks className="text-purple-600 mr-2" />
                  <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Status Information</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Status</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>
                        {selectedLog.statusUpdatedAt 
                          ? new Date(selectedLog.statusUpdatedAt).toLocaleString()
                          : 'Not Available'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {selectedLog.additionalRemarks && (
                  <div className="mt-6">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Update Remarks</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.additionalRemarks}</p>
                    </div>
                  </div>
                )}

                {selectedLog.statusUpdatedBy && (
                  <div className="mt-4">
                    <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status Updated By</label>
                    <div className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <p className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedLog.statusUpdatedBy.username || selectedLog.statusUpdatedBy.name}</p>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`flex justify-end space-x-3 p-6 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => setShowStatusModal(false)}
                className={`px-6 py-3 border rounded-lg transition-colors font-medium ${isDarkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
              >
                {selectedLog.status === 'Pending' && !selectedLog.isStatusUpdated ? 'Cancel' : 'Close'}
              </button>
              {selectedLog.status === 'Pending' && !selectedLog.isStatusUpdated && (
                <button
                  onClick={handleStatusSubmit}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                >
                  Update Status
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extra Remark Modal */}
      {showExtraRemarkModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Modal Header */}
            <div className={`flex justify-between items-center p-6 border-b ${isDarkMode ? 'border-gray-700 bg-gradient-to-r from-yellow-900/30 to-orange-900/30' : 'border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50'}`}>
              <div>
                <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Add Extra Remark</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Log ID: <span className="font-semibold text-blue-600">{selectedLog.id}</span> - 
                  <span className="font-semibold ml-1 text-yellow-600">Pending</span>
                </p>
              </div>
              <button
                onClick={() => setShowExtraRemarkModal(false)}
                className={`rounded-lg p-2 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className={`rounded-xl p-6 border mb-6 ${isDarkMode ? 'bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-yellow-800' : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200'}`}>
                <div className="flex items-center mb-4">
                  <FaPlus className="text-orange-600 mr-2" />
                  <h4 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Add Extra Remark</h4>
                </div>
                
                <div className={`rounded-lg p-4 border mb-4 ${isDarkMode ? 'bg-gray-700 border-orange-800' : 'bg-white border-orange-200'}`}>
                  <p className={`font-medium mb-2 ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                    You are adding an extra remark to this pending activity log.
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Extra remarks can be used to add additional notes or updates before changing the status.
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Extra Remark *</label>
                  <textarea
                    value={extraRemark}
                    onChange={(e) => setExtraRemark(e.target.value)}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300'}`}
                    rows="4"
                    placeholder="Enter your extra remark here..."
                    required
                  ></textarea>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>This remark will be timestamped and added to the log.</p>
                </div>

                {/* Show existing extra remarks if any */}
                {selectedLog.extraRemarks && selectedLog.extraRemarks.length > 0 && (
                  <div className="mt-6">
                    <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Existing Extra Remarks</h5>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {selectedLog.extraRemarks.map((remark, index) => (
                        <div key={index} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                          <p className={`leading-relaxed text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{remark.text}</p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Added on: {new Date(remark.date).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`flex justify-end space-x-3 p-6 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => setShowExtraRemarkModal(false)}
                className={`px-6 py-3 border rounded-lg transition-colors font-medium ${isDarkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleExtraRemarkSubmit}
                className="px-6 py-3 bg-gradient-to-r from-orange-600 to-yellow-600 text-white rounded-lg hover:from-orange-700 hover:to-yellow-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                Add Extra Remark
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogsReports;